import {
  JournalEntryLineRecord,
  JournalEntryRecord,
  JournalPostingScope,
  JournalSourceModule,
} from '../../../../types/accounting';

export interface JournalLineFormState {
  id?: string;
  accountId: string;
  description: string;
  debit: string;
  credit: string;
}

export interface JournalEntryFormState {
  entryDate: string;
  postingScope: JournalPostingScope;
  outletId: string;
  referenceType: string;
  referenceId: string;
  memo: string;
  sourceModule: JournalSourceModule;
  lines: JournalLineFormState[];
}

export const JOURNAL_SOURCE_OPTIONS: {
  value: JournalSourceModule;
  label: string;
}[] = [{ value: 'manual', label: 'Manual' }];

export const POSTING_SCOPE_OPTIONS: {
  value: JournalPostingScope;
  label: string;
}[] = [
  { value: 'company', label: 'Company' },
  { value: 'outlet', label: 'Outlet' },
];

export const createEmptyJournalLine = (): JournalLineFormState => ({
  accountId: '',
  description: '',
  debit: '',
  credit: '',
});

export const createEmptyJournalEntryForm = (
  outletId?: string | null,
): JournalEntryFormState => ({
  entryDate: new Date().toISOString().slice(0, 10),
  postingScope: outletId ? 'outlet' : 'company',
  outletId: outletId ?? '',
  referenceType: '',
  referenceId: '',
  memo: '',
  sourceModule: 'manual',
  lines: [createEmptyJournalLine(), createEmptyJournalLine()],
});

export const createJournalEntryFormFromRecord = (
  entry: JournalEntryRecord,
  lines: JournalEntryLineRecord[],
): JournalEntryFormState => ({
  entryDate: entry.entry_date,
  postingScope: entry.posting_scope,
  outletId: entry.outlet_id ?? '',
  referenceType: entry.reference_type ?? '',
  referenceId: entry.reference_id ?? '',
  memo: entry.memo ?? '',
  sourceModule: entry.source_module,
  lines: lines.map((line) => ({
    id: line.id,
    accountId: line.account_id,
    description: line.description ?? '',
    debit: line.debit ? String(line.debit) : '',
    credit: line.credit ? String(line.credit) : '',
  })),
});

export const formatJournalStatusLabel = (
  status: JournalEntryRecord['status'],
) => status.charAt(0).toUpperCase() + status.slice(1);

export const formatPostingScopeLabel = (
  scope: JournalPostingScope,
  outletName?: string | null,
) => (scope === 'outlet' ? outletName || 'Outlet' : 'Company');

export const formatSourceModuleLabel = (value: JournalSourceModule) =>
  JOURNAL_SOURCE_OPTIONS.find((option) => option.value === value)?.label ||
  value;

export const calculateLineTotals = (lines: JournalLineFormState[]) =>
  lines.reduce(
    (totals, line) => ({
      debit: totals.debit + Number(line.debit || 0),
      credit: totals.credit + Number(line.credit || 0),
    }),
    { debit: 0, credit: 0 },
  );
